import React, { useEffect, useState } from 'react';
import { EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined, SafetyOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Role2 } from '../../../models';
import { NewRole2Page, RolePermissionsSummary } from '.';
import { EntityChangeLog } from '../../reports';
import { TableNameColumn } from '../../../elements';

export function Roles2Page() {
  const { Content } = Layout;
  const { Text } = Typography;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role2[]>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = () => {
    setLoading(true);
    apiClient
      .get('authorization/roles')
      .then(response => {
        setRoles(response.data as Role2[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadRoles();
  };

  const onRoleAdded = () => {
    closeAddModal();
    loadRoles();
  };

  const deleteRole = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`authorization/roles/${id}`)
      .then(() => {
        loadRoles();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const confirmDeleteRole = (id: string) => {
    const onOk = () => deleteRole(id);
    Modal.confirm({
      title: 'Rol sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Rolü silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    } else if (e.key === 'del') {
      confirmDeleteRole(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, role: Role2) => (
        <TableNameColumn name={name} description={role.description} path={'/admin/roles2/' + role.id} />
      ),
    },
    {
      title: 'Yetkiler',
      dataIndex: 'permissions2',
      key: 'permissions',
      render: (_: any[], role: Role2) => <RolePermissionsSummary role={role} maxDisplay={10} />,
    },
    {
      title: 'Kullanıcılar',
      dataIndex: 'userCount',
      key: 'userCount',
      render: (userCount: number) => (userCount === 0 ? '-' : userCount),
    },
    {
      title: 'Kullanıcı grupları',
      dataIndex: 'userGroupCount',
      key: 'userGroupCount',
      render: (userGroupCount: number) => (userGroupCount === 0 ? '-' : userGroupCount),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, role: Role2) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(role.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <SafetyOutlined /> Roller (yeni)
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="add" onClick={openAddModal} icon={<PlusOutlined />} type="primary">
            Yeni rol
          </Button>,
        ]}
      />
      <Content>
        <Table
          columns={tableColumns}
          rowKey={row => row.id}
          dataSource={roles}
          loading={loading}
          scroll={{ x: '100%' }}
          pagination={false}
        />
      </Content>
      <Modal title="Yeni rol" visible={addModalOpen} footer={null} width={'60%'} onCancel={closeAddModal} destroyOnClose={true}>
        <NewRole2Page onSaved={onRoleAdded} />
      </Modal>
      <Modal
        title="Rol değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Role2'} recordId={changeLogModalOpen} />
      </Modal>
    </>
  );
}
